/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaPalette, 
  FaImages, 
  FaGlobe, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaTimes,
  FaCloudUploadAlt,
  FaTrash,
  FaPlus
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../apis/axiosConfig';
import './EditStoreLocator.css';

const EditStoreLocator = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // 0 to 2
  const [loading, setLoading] = useState(true);

  // Tab configurations:
  // - Basic Info
  // - Banner Configuration
  // - SEO Manager
  const [sections, setSections] = useState([
    { id: 0, section: 'Basic Info', icon: FaPalette, status: 1, subtitle: 'Default', validation: 'validation-success' },
    { id: 1, section: 'Banner Configuration', icon: FaImages, status: 1, subtitle: 'Default', validation: 'validation-success' },
    { id: 2, section: 'SEO Manager', icon: FaGlobe, status: 1, subtitle: 'Default', validation: 'validation-success' }
  ]);

  // Tab 0: Basic Info
  const [basicInfo, setBasicInfo] = useState({
    title: 'Aristo Showroom Locator',
    description: '',
    status: 1
  });

  // Tab 1: Banner Configuration Slides list
  const [bannerSlides, setBannerSlides] = useState([
    { id: '1', heading: '', description: '', image: '', alt: '', status: 1, sort: '1' }
  ]);

  // Tab 2: SEO Manager
  const [seo, setSeo] = useState({
    meta_title: 'Find Aristo India Showroom | Sliding Wardrobes & Kitchens',
    meta_description: '',
    meta_keywords: []
  });
  const [keywordInput, setKeywordInput] = useState('');

  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchStoreLocatorData();
  }, []);

  const fetchStoreLocatorData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/pages/store-locator');
      if (res && res.data && res.data.success) {
        const { page } = res.data;

        if (page) {
          // 1. Set Page properties
          setBasicInfo({
            title: page.page_heading || 'Aristo Showroom Locator',
            description: page.short_description || '',
            status: page.status === 1 ? 1 : 0
          });

          // 2. Parse banner slides
          try {
            const slides = typeof page.banner === 'string' ? JSON.parse(page.banner) : page.banner;
            if (Array.isArray(slides) && slides.length > 0) {
              setBannerSlides(slides);
            }
          } catch (e) {
            console.error("Error parsing banner content:", e);
          }

          // 3. Parse SEO content
          const keywordsArray = page.meta_keywords 
            ? page.meta_keywords.split(',').map(k => k.trim()).filter(Boolean)
            : [];
          setSeo({
            meta_title: page.meta_title || '',
            meta_description: page.meta_description || '',
            meta_keywords: keywordsArray
          });

          // 4. Update section status checkmarks
          const updatedSec = [...sections];
          updatedSec[0].status = page.status === 1 ? 1 : 0;
          updatedSec[0].validation = page.status === 1 ? 'validation-success' : 'validation-error';
          
          updatedSec[1].status = page.banner_status === 1 ? 1 : 0;
          updatedSec[1].validation = page.banner_status === 1 ? 'validation-success' : 'validation-error';
          
          updatedSec[2].status = page.status === 1 ? 1 : 0;
          updatedSec[2].validation = page.status === 1 ? 'validation-success' : 'validation-error';
          setSections(updatedSec);
        }
      }
    } catch (err) {
      console.error("Error loading Store Locator configuration:", err);
      toast.error("Failed to load Store Locator page data");
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('blob:') || image.startsWith('http')) return image;
    const backendHost = (process.env.REACT_APP_BACKEND || (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.split('/api')[0]) || 'http://localhost:8000').replace(/\/$/, '');
    return `${backendHost}/uploads/${image}`;
  };

  // Toggle switch sidebar section status
  const handleToggleSectionStatus = (idx, e) => {
    e.stopPropagation();
    const updated = [...sections];
    const newStatus = updated[idx].status === 1 ? 0 : 1;
    updated[idx].status = newStatus;
    updated[idx].validation = newStatus === 1 ? 'validation-success' : 'validation-error';
    
    if (idx === 0) {
      setBasicInfo(prev => ({ ...prev, status: newStatus }));
    }
    
    setSections(updated);
  };

  // Tag inputs helper
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = keywordInput.trim().replace(/,$/, '');
      if (val && !seo.meta_keywords.includes(val)) {
        setSeo({
          ...seo,
          meta_keywords: [...seo.meta_keywords, val]
        });
      }
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (indexToRemove) => {
    setSeo({
      ...seo,
      meta_keywords: seo.meta_keywords.filter((_, idx) => idx !== indexToRemove)
    });
  };

  // Banner Slides CRUD
  const handleAddSlide = () => {
    const newSlide = {
      id: String(bannerSlides.length + 1),
      heading: '',
      description: '',
      image: '',
      alt: '',
      status: 1,
      sort: '1'
    };
    setBannerSlides([...bannerSlides, newSlide]);
  };

  const handleRemoveSlide = (idx) => {
    if (bannerSlides.length === 1) {
      toast.warning('At least one slide configuration is required.');
      return;
    }
    setBannerSlides(bannerSlides.filter((_, i) => i !== idx));
  };

  const handleSlideImageChange = async (e, slideIdx) => {
    const file = e.target.files[0];
    if (!file) return;

    const tempUrl = URL.createObjectURL(file);
    const updated = [...bannerSlides];
    updated[slideIdx].image = tempUrl;
    updated[slideIdx]._file = file;
    setBannerSlides(updated);

    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await axiosInstance.post('/products/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data && res.data.filename) {
        const finalUrl = res.data.filename;
        const fresh = [...bannerSlides];
        fresh[slideIdx].image = finalUrl;
        delete fresh[slideIdx]._file;
        setBannerSlides(fresh);
        toast.success(`Slide ${slideIdx + 1} image uploaded!`);
      }
    } catch (err) {
      console.error(err);
      toast.error('Image upload failed');
    }
  };

  // Save Settings
  const handleSaveSettings = async () => {
    if (!basicInfo.title.trim()) {
      toast.warning('Please enter Page Title!');
      return;
    }
    if (!basicInfo.description.trim()) {
      toast.warning('Please enter Short Description!');
      return;
    }

    // Clean banner slides
    const cleanedSlides = bannerSlides.map(s => {
      const copy = { ...s };
      delete copy._file;
      return copy;
    });

    try {
      const payload = {
        page_heading: basicInfo.title,
        short_description: basicInfo.description,
        banner: cleanedSlides,
        banner_status: sections[1].status,
        meta_title: seo.meta_title,
        meta_description: seo.meta_description,
        meta_keywords: seo.meta_keywords.join(','),
        status: basicInfo.status
      };

      const res = await axiosInstance.patch('/pages/store-locator', payload);
      if (res.data && res.data.success) {
        toast.success('Store Locator saved successfully!');
        
        // Update checkmarks status
        const updatedSec = [...sections];
        updatedSec[0].validation = basicInfo.status === 1 ? 'validation-success' : 'validation-error';
        updatedSec[1].validation = sections[1].status === 1 ? 'validation-success' : 'validation-error';
        updatedSec[2].validation = basicInfo.status === 1 ? 'validation-success' : 'validation-error';
        setSections(updatedSec);
      } else {
        toast.error(res.data.message || 'Error updating Store Locator');
      }
    } catch (e) {
      console.error("Save error:", e);
      toast.error('Server error while saving Store Locator configuration');
    }
  };

  if (loading) {
    return (
      <div className="edit-store-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <h3 style={{ color: '#c4a882' }}>Loading Store Locator Configuration...</h3>
      </div>
    );
  }

  return (
    <div className="edit-store-container animate-fade-up">
      <div className="edit-store-header">
        <h1 className="edit-store-title">Store Locator</h1>
      </div>

      <div className="edit-store-grid">
        {/* Sticky Left Sidebar */}
        <div className="edit-store-sidebar">
          <ul className="edit-store-nav-list">
            {sections.map((sec, idx) => {
              const IconComp = sec.icon;
              return (
                <li 
                  key={sec.id}
                  className={`edit-store-nav-item ${activeTab === idx ? 'active' : ''}`}
                  onClick={() => setActiveTab(idx)}
                >
                  <div className="edit-store-nav-left">
                    <span className="edit-store-nav-icon"><IconComp /></span>
                    <div className="edit-store-nav-text">
                      <span className="nav-item-title">{sec.section}</span>
                      <span className="nav-item-subtitle">{sec.subtitle}</span>
                    </div>
                  </div>

                  <div className="edit-store-nav-right">
                    <span className={`status-indicator ${sec.validation}`}>
                      {sec.validation === 'validation-success' && <FaCheckCircle />}
                      {sec.validation === 'validation-error' && <FaTimesCircle />}
                    </span>

                    <label className="switch-toggle" onClick={(e) => e.stopPropagation()}>
                      <input 
                        type="checkbox" 
                        checked={sec.status === 1}
                        onChange={(e) => handleToggleSectionStatus(idx, e)}
                      />
                      <span className="switch-slider"></span>
                    </label>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Scrollable Right Panel content */}
        <div className="edit-store-content-panel">
          
          {/* TAB 0: Basic Info */}
          {activeTab === 0 && (
            <div className="tab-pane animate-fade-up">
              <h2 className="edit-store-panel-title">Basic Info</h2>
              
              <div className="store-card-block">
                <div className="form-group-custom">
                  <label className="label-custom">Page Title <span className="req">*</span></label>
                  <input 
                    type="text" 
                    className="input-custom" 
                    placeholder="Enter Page Title..."
                    value={basicInfo.title}
                    onChange={(e) => setBasicInfo({ ...basicInfo, title: e.target.value })}
                  />
                </div>

                <div className="form-group-custom">
                  <label className="label-custom">Short Description <span className="req">*</span></label>
                  <textarea 
                    className="textarea-custom" 
                    rows={5}
                    placeholder="Enter Short Description..."
                    value={basicInfo.description}
                    onChange={(e) => setBasicInfo({ ...basicInfo, description: e.target.value })}
                  />
                </div>

                <div className="form-group-custom" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem' }}>
                  <label className="label-custom" style={{ marginBottom: 0 }}>Page Active Status</label>
                  <label className="switch-toggle">
                    <input 
                      type="checkbox" 
                      checked={basicInfo.status === 1}
                      onChange={(e) => {
                        const newStatus = e.target.checked ? 1 : 0;
                        setBasicInfo({ ...basicInfo, status: newStatus });
                        
                        const updated = [...sections];
                        updated[0].status = newStatus;
                        updated[0].validation = newStatus === 1 ? 'validation-success' : 'validation-error';
                        updated[2].status = newStatus;
                        updated[2].validation = newStatus === 1 ? 'validation-success' : 'validation-error';
                        setSections(updated);
                      }}
                    />
                    <span className="switch-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* TAB 1: Banner Configuration */}
          {activeTab === 1 && (
            <div className="tab-pane animate-fade-up">
              <h2 className="edit-store-panel-title">Banner Configuration</h2>
              
              {sections[1].status === 0 ? (
                <div className="oops-disabled-overlay animate-fade-up">
                  <div className="oops-card">
                    <div className="oops-icon">✖</div>
                    <h3 className="oops-title">Oops!</h3>
                    <p className="oops-message">Please enable or activate Banner Configuration to modify the content.</p>
                    <button 
                      type="button" 
                      className="btn-enable-oops"
                      onClick={() => {
                        const updated = [...sections];
                        updated[1].status = 1;
                        updated[1].validation = 'validation-success';
                        setSections(updated);
                      }}
                    >
                      Enable / Activate
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {bannerSlides.map((slide, sIdx) => (
                    <div key={sIdx} className="store-card-block" style={{ marginBottom: '1.5rem', position: 'relative' }}>
                      
                      {/* Slide Header Row */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '0.5rem', marginBottom: '1.25rem' }}>
                        <h3 style={{ fontSize: '1.1rem', margin: 0, color: '#c4a882' }}>Slide {sIdx + 1}</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <label className="switch-toggle" style={{ transform: 'scale(0.85)', marginBottom: 0 }}>
                            <input 
                              type="checkbox" 
                              checked={slide.status === 1}
                              onChange={(e) => {
                                const u = [...bannerSlides];
                                u[sIdx].status = e.target.checked ? 1 : 0;
                                setBannerSlides(u);
                              }}
                            />
                            <span className="switch-slider"></span>
                          </label>
                          <button 
                            type="button" 
                            style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', padding: 0 }}
                            onClick={() => handleRemoveSlide(sIdx)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>

                      {/* Unified Grid Layout - Alt Text under Title on right */}
                      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                        <div>
                          <label className="label-custom">Slider Image <span className="req">*</span></label>
                          <div 
                            style={{ height: '180px', border: '2px dashed rgba(196,168,130,0.3)', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', background: slide.image ? '#ffffff' : 'transparent' }}
                            onClick={() => fileInputRefs.current[`store_slide_${sIdx}`]?.click()}
                          >
                            {slide.image ? (
                              <>
                                <img src={getImageUrl(slide.image)} alt={slide.alt} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '10px' }} />
                                <div className="image-delete-overlay" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444', opacity: 0, transition: 'opacity 0.2s' }} onClick={(e) => { e.stopPropagation(); const u = [...bannerSlides]; u[sIdx].image = ''; setBannerSlides(u); }}>
                                  <FaTrash size={20} />
                                </div>
                              </>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#94a3b8' }}>
                                <FaCloudUploadAlt size={36} style={{ color: '#c4a882', marginBottom: '0.5rem' }} />
                                <span style={{ fontSize: '0.85rem' }}>Upload Banner</span>
                              </div>
                            )}
                          </div>
                          <input 
                            type="file" 
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={el => fileInputRefs.current[`store_slide_${sIdx}`] = el}
                            onChange={(e) => handleSlideImageChange(e, sIdx)}
                          />
                          <span className="helper-text" style={{ display: 'block', marginBottom: '1rem' }}>Image dimensions 800 × 500 px.</span>
                        </div>

                        <div>
                          <div className="form-group-custom">
                            <label className="label-custom">Slider Image Title <span className="req">*</span></label>
                            <input 
                              type="text" 
                              className="input-custom"
                              placeholder="Slider Image Title"
                              value={slide.heading || ''}
                              onChange={(e) => {
                                const u = [...bannerSlides];
                                u[sIdx].heading = e.target.value;
                                setBannerSlides(u);
                              }}
                            />
                          </div>

                          <div className="form-group-custom">
                            <label className="label-custom">Alt Text <span className="req">*</span></label>
                            <input 
                              type="text" 
                              className="input-custom"
                              placeholder="Alt Text"
                              value={slide.alt || ''}
                              onChange={(e) => {
                                const u = [...bannerSlides];
                                u[sIdx].alt = e.target.value;
                                setBannerSlides(u);
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Standalone Description Text Box */}
                      <div className="form-group-custom">
                        <label className="label-custom">Slider Image Description (Optional)</label>
                        <textarea 
                          className="textarea-custom"
                          rows={4}
                          placeholder="Slider Image Description"
                          value={slide.description || ''}
                          onChange={(e) => {
                            const u = [...bannerSlides];
                            u[sIdx].description = e.target.value;
                            setBannerSlides(u);
                          }}
                        />
                      </div>

                    </div>
                  ))}

                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
                    <button 
                      type="button" 
                      className="btn-save" 
                      style={{ background: 'transparent', border: '1px dashed #c4a882', color: '#c4a882', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: 'none' }}
                      onClick={handleAddSlide}
                    >
                      <FaPlus /> Add Banner Slide
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* TAB 2: SEO Manager */}
          {activeTab === 2 && (
            <div className="tab-pane animate-fade-up">
              <h2 className="edit-store-panel-title">SEO Manager</h2>
              
              <div className="store-card-block">
                <div className="form-group-custom">
                  <label className="label-custom">Meta Title <span className="req">*</span></label>
                  <input 
                    type="text" 
                    className="input-custom" 
                    placeholder="Enter Meta Title..."
                    value={seo.meta_title}
                    onChange={(e) => setSeo({ ...seo, meta_title: e.target.value })}
                  />
                </div>

                <div className="form-group-custom">
                  <label className="label-custom">Meta Description <span className="req">*</span></label>
                  <textarea 
                    className="textarea-custom" 
                    rows={4}
                    placeholder="Enter Meta Description..."
                    value={seo.meta_description}
                    onChange={(e) => setSeo({ ...seo, meta_description: e.target.value })}
                  />
                </div>

                <div className="form-group-custom">
                  <label className="label-custom">Meta Keywords</label>
                  
                  {/* Tag Pill Selector */}
                  <div className="keywords-pill-container">
                    {seo.meta_keywords.map((tag, idx) => (
                      <span key={idx} className="keyword-pill">
                        {tag}
                        <button 
                          type="button" 
                          className="keyword-pill-remove" 
                          onClick={() => handleRemoveKeyword(idx)}
                        >
                          <FaTimes size={10} />
                        </button>
                      </span>
                    ))}
                    <input 
                      type="text" 
                      className="keywords-pills-input" 
                      placeholder={seo.meta_keywords.length === 0 ? "Type keyword and press Enter..." : "Add keyword..."}
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={handleKeywordKeyDown}
                    />
                  </div>
                  <span className="helper-text">Type a keyword and press Enter or Comma (,) to add. Click &quot;x&quot; to remove.</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Action Bar */}
      <div className="edit-store-footer">
        <button 
          type="button" 
          className="btn-cancel"
          onClick={() => navigate('/page-list')}
        >
          Cancel
        </button>
        <button 
          type="button" 
          className="btn-save"
          onClick={handleSaveSettings}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default EditStoreLocator;
